export enum LoginFlag {
  NONE = 0,
  TEMP_PASSWORD,
  PASSWORD_EXPIRED,
}

export enum LoginError {
  CREDENTIALS = 1,
  PARTNER_INACTIVE,
  USER_INACTIVE,
  TEMP_PASSWORD_EXPIRED,
}

export enum SsoError {
  PROVIDER = 1,
  USER_NOT_FOUND,
  USER_INACTIVE,
  USER_NOT_SSO,
  PARTNER_INACTIVE,
}

export enum UserError {
  ALREADY_EXISTS = 1,
  DOES_NOT_EXIST,
}

export interface Profile {
  id: number;
  full_name: string;
  email: string;
  role: number;
  sso: number;
}

export interface ProfileUpdate {
  full_name: string;
  email: string;
  password: string;
}

export interface ProfileUpdateOut {
  result?: Profile;
  error?: UserError;
  password_error?: PasswordError;
}

export interface User {
  id: number;
  full_name: string;
  email: string;
  login_date: string;
  is_active: boolean;
  role: number;
  partner_id: number;
  sso: number;
}

export interface UserCreate {
  full_name: string;
  email: string;
  password: string;
  is_active: boolean;
  role: number;
  partner_id: number;
  sso: number;
}

export interface LoginResult {
  token: Token;
  flag: LoginFlag;
}

export interface LoginOut {
  result?: LoginResult;
  error?: LoginError;
}

export interface SsoOut {
  result?: Token;
  error?: SsoError;
}

export interface UserOut {
  result?: User;
  error?: UserError;
  password_error?: PasswordError;
}

export enum PasswordError {
  MIN_LENGTH = 1,
  LOW_COMPLEXITY,
  REUSED_LAST_PASSWORDS,
  CONTAINS_USERNAME_OR_EMAIL,
  CONTAINS_BANNED_WORDS,
  NOT_UNIQUE,
}

export interface PasswordIn {
  old_password: string;
  new_password: string;
}

export interface PasswordRecoverIn {
  email: string;
}

export interface PasswordOut {
  result?: boolean;
  error?: LoginError;
  password_error?: PasswordError;
}

export interface Token {
  access_token: string;
  token_type: string;
}

export enum ConsumerError {
  ALREADY_EXISTS = 1,
  DOES_NOT_EXIST,
}

export interface Consumer {
  id: number;
  name: string;
  email: string;
  registration_date: string;
  login_date: string;
  is_active: boolean;
  sso: number;
}

export interface ConsumerCreate {
  name: string;
  email: string;
  password: string;
  is_temp_password: boolean;
  is_active: boolean;
  sso: number;
}

export interface ConsumerOut {
  result?: Consumer;
  error?: ConsumerError;
  password_error?: PasswordError;
}

export interface Filter {
  id: number;
  name: string;
  filters: FilterItem[];
}

export interface FilterCreate {
  name: string;
  filters: FilterItem[];
}

export interface FilterItem {
  type: string;
  name: string;
  value: any;
}

export interface Version {
  app_version: string;
}

export interface Scan {
  id: number;
  timestamp: string;
  consumer: string;
  product: string;
  image: string;
  information: string;
  note: string;
  result: number;
  address: string;
  country_abbr: string;
  geo_lat: number;
  geo_lng: number;
  phone_model: string;
  os_version: string;
  app_version: string;
}

export interface ScanStats {
  main: BarStatsItem[];
  brands: PieStatsItem[];
}

export interface BarStatsItem {
  month: string;
  authentic: number;
  suspect: number;
}

export interface PieStatsItem {
  product_id: number;
  authentic: number;
  suspect: number;
}

export interface ConsumerStats {
  month: string;
  registration: number;
}

export interface StatsData {
  bar: string;
  pies: string[];
  filters: FilterItem[];
}

export interface Target {
  id: number;
  app_id: number;
  product_id: number;
  model: string;
  kind: string;
}

export interface Product {
  id: number;
  name: string;
  image: string;
  description: string;
}

export enum SettingsFlag {
  NONE = 0,
  HIDE_INFO = 1,
  HIDE_LOCATION = 2,
  USER_ANONYMIZATION = 4,
}

export interface App {
  id: number;
  abbr: string;
  name: string;
  description: string;
  consumer: number;
  settings: SettingsFlag;
  sso: number;
}

export interface AppCreate {
  abbr: string;
  name: string;
  description: string;
  consumer: number;
  settings: SettingsFlag;
  sso: number;
}

export interface CountryCluster {
  id: number;
  name: string;
  countries: string;
  is_active: boolean;
  partner_id: number;
}

export interface CountryClusterCreate {
  name: string;
  countries: string;
  is_active: boolean;
  partner_id: number;
}

export interface MapCluster {
  geo_lat: number;
  geo_lng: number;
  authentic_count: number;
  suspect_count: number;
  accuracy: number;
}

export interface Partner {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export interface PartnerCreate {
  name: string;
  description: string;
  is_active: boolean;
}

export interface MapData {
  image: string;
  filters: FilterItem[];
  sort: string;
  desc: boolean;
  bounds: string;
  geo_lat: number;
  geo_lng: number;
  accuracy: number;
}
